import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  UploadCloud, Eye, EyeOff, ShieldCheck, Coins, AlertTriangle, Play, Download,
  RotateCcw, Loader2, Film, ScanFace, Car, Focus, Smile, CheckCircle2, Timer, Sparkles,
  MousePointerClick, Pause, XCircle, Zap,
} from "lucide-react";
import { toast } from "sonner";
import ReviewEditor from "../components/ReviewEditor";
import ChunkPanel from "../components/ChunkPanel";
import DriveCard from "../components/DriveCard";
import { BLUR_OPTIONS, IMAGES, getCredits, setCredits } from "../mock/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB

const ICONS = { ScanFace, Car, Focus, Smile, ShieldCheck, MousePointerClick };

/* motivo probable cuando un trabajo deja de dar señales de vida */
const stallHint = (step = "") => {
  const s = step.toLowerCase();
  if (s.includes("copia de trabajo") || s.includes("proxy")) return "ffmpeg está comprimiendo un vídeo largo y ha dejado de responder";
  if (s.includes("detectando")) return "un segmento con mucho tráfico puede haber saturado al detector de IA";
  if (s.includes("matrícula") || s.includes("ocr") || s.includes("escaneando")) return "la búsqueda/lectura de matrículas se ha quedado atascada en un tramo";
  if (s.includes("difuminado")) return "el render de un trozo del vídeo no responde";
  if (s.includes("exportando") || s.includes("costura") || s.includes("verificando")) return "la exportación final se ha quedado bloqueada";
  return "el proceso ha dejado de responder";
};

/* ---------- etiqueta esquina ---------- */
const CornerTag = ({ label, tone = "light" }) => (
  <span className={`absolute top-3 left-3 z-20 px-3 py-1.5 rounded-full text-[10.5px] font-extrabold tracking-[0.14em] uppercase select-none ${
    tone === "orange" ? "bg-[#F26F21] text-white" : "bg-white/95 text-[#241E18] border border-[#F0E7DD]"
  }`}>
    {label}
  </span>
);

/* ---------- parches de simulación (solo vista previa) ---------- */
const SimPatches = ({ option }) => {
  const faces = [
    { top: "20%", left: "28%", w: "11%", cls: "patch-drift-a", delay: "0s" },
    { top: "26%", left: "58%", w: "9%", cls: "patch-drift-b", delay: "1.4s" },
  ];
  const plates = [
    { top: "66%", left: "40%", w: "17%", h: "9%", cls: "patch-drift-b", delay: "0.6s" },
    { top: "58%", left: "12%", w: "12%", h: "7%", cls: "patch-drift-a", delay: "2.1s" },
  ];
  return (
    <>
      {(option === "faces" || option === "both" || option === "emoji") &&
        faces.map((s, i) => (
          <div key={`f${i}`} className={`absolute z-10 pointer-events-none ${s.cls}`}
            style={{ top: s.top, left: s.left, width: s.w, aspectRatio: "1/1.1", animationDelay: s.delay }}>
            {option === "emoji" ? (
              <span className="flex items-center justify-center w-full h-full text-[clamp(26px,4vw,54px)] drop-shadow-lg">{"\uD83D\uDE0E"}</span>
            ) : (
              <div className="w-full h-full rounded-xl backdrop-blur-xl bg-white/5 border border-white/15" />
            )}
          </div>
        ))}
      {(option === "plates" || option === "both") &&
        plates.map((s, i) => (
          <div key={`p${i}`} className={`absolute z-10 pointer-events-none rounded-lg backdrop-blur-xl bg-white/5 border border-white/15 ${s.cls}`}
            style={{ top: s.top, left: s.left, width: s.w, height: s.h, animationDelay: s.delay }} />
        ))}
    </>
  );
};

/* ---------- pasos del asistente ---------- */
const Wizard = ({ current }) => {
  const steps = ["Sube tu vídeo", "Configura", "Descarga"];
  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2.5">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-extrabold transition-colors ${
              i < current ? "bg-[#2FA45A] text-white" : i === current ? "bg-[#F26F21] text-white" : "bg-[#F5EDE3] text-[#A69C90]"
            }`}>
              {i < current ? <CheckCircle2 size={16} /> : i + 1}
            </span>
            <span className={`text-[13.5px] font-bold hidden sm:block ${i === current ? "text-[#241E18]" : "text-[#A69C90]"}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <span className="w-10 sm:w-16 h-[2px] mx-3 rounded bg-[#F0E7DD]" />}
        </React.Fragment>
      ))}
    </div>
  );
};

const PROC_FALLBACK_STEP = "Procesando vídeo";

const EditorPage = () => {
  const [phase, setPhase] = useState("idle"); // idle | uploading | config | processing | done
  const [videoId, setVideoId] = useState(null);
  const [videoName, setVideoName] = useState("");
  const [duration, setDuration] = useState(0);
  const [uploadPct, setUploadPct] = useState(0);
  const [option, setOption] = useState("plates");
  const [hasAnalysis, setHasAnalysis] = useState(false);
  const [jobPaused, setJobPaused] = useState(false);
  const [jobStalled, setJobStalled] = useState(0);
  const [framesDone, setFramesDone] = useState(0);
  const [framesTotal, setFramesTotal] = useState(0);
  const [etaMin, setEtaMin] = useState(null);
  const rateRef = useRef({ t: 0, f: 0, rate: 0 });

  /* estima minutos restantes con la velocidad real de fotogramas (media suavizada) */
  const updateEta = (done, total) => {
    const now = Date.now();
    const r = rateRef.current;
    if (done < r.f) { r.rate = 0; r.t = now; r.f = done; setEtaMin(null); return; }
    if (r.t && done > r.f) {
      const inst = (done - r.f) / ((now - r.t) / 1000);
      r.rate = r.rate ? r.rate * 0.7 + inst * 0.3 : inst;
    }
    if (done !== r.f) { r.t = now; r.f = done; }
    if (r.rate > 0.5 && total > done) setEtaMin(Math.max(1, Math.ceil((total - done) / r.rate / 60)));
    else if (total <= done) setEtaMin(null);
  };
  const [style, setStyle] = useState("box"); // box = recuadro solido | pixel = pixelado
  const [strength, setStrength] = useState(3); // 1 suave | 2 media | 3 fuerte
  const [precision, setPrecision] = useState("fast"); // fast | max
  const [chunkMinutes, setChunkMinutes] = useState(1); // tramos de 1/2/5 min
  const [reviewChunk, setReviewChunk] = useState(null); // {start,end,index,count}
  const [previewOn, setPreviewOn] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stepLabel, setStepLabel] = useState("");
  const [showOriginal, setShowOriginal] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [credits, setCreditsState] = useState(getCredits());
  const [forensic, setForensic] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  /* ---------- cronometro del proceso ---------- */
  const [procStart, setProcStart] = useState(null); // Date de inicio
  const [procEnd, setProcEnd] = useState(null);     // Date de fin
  const [elapsed, setElapsed] = useState(0);        // segundos en vivo
  const [procKind, setProcKind] = useState("");     // "Análisis" | "Renderizado"
  const [procLog, setProcLog] = useState([]);       // historial de procesos finalizados
  const startRef = useRef(null);
  const kindRef = useRef("");

  const fmtDateTime = (d) =>
    d
      ? d.toLocaleString("es-ES", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit", second: "2-digit",
        })
      : "—";

  const fmtElapsed = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const startTimer = (kind) => {
    const now = new Date();
    startRef.current = now;
    kindRef.current = kind;
    setProcKind(kind);
    setProcStart(now);
    setProcEnd(null);
    setElapsed(0);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - now.getTime()) / 1000));
    }, 1000);
  };

  const stopTimer = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    const end = new Date();
    setProcEnd(end);
    if (startRef.current) {
      setProcLog((prev) => [...prev, { kind: kindRef.current, start: startRef.current, end }]);
    }
  };

  const fmtTime = (d) => d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const fmtDate = (d) => d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });

  const fmtForDur = (s) => {
    const m = Math.floor(s / 60), sec = s - m * 60;
    return `${m}:${sec.toFixed(3).padStart(6, "0")}`;
  };

  const ForensicPanel = () =>
    !forensic ? null : (
      <div
        data-testid="forensic-report"
        className={`mt-4 rounded-2xl border px-5 py-4 ${
          forensic.ok ? "bg-[#EAF7EE] border-[#BFE5CB]" : "bg-[#FDECEA] border-[#F5C6C0]"
        }`}
      >
        <p className="text-[11px] font-extrabold tracking-[0.14em] uppercase text-[#57503F] mb-2 inline-flex items-center gap-2">
          <ShieldCheck size={14} className={forensic.ok ? "text-[#2FA45A]" : "text-[#D93025]"} />
          Modo Forense · Inmutabilidad temporal
        </p>
        <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1.5 text-[12.5px] font-semibold text-[#57503F] tabular-nums">
          <p data-testid="forensic-frames">Frames originales: <b className="text-[#241E18]">{forensic.frames_original}</b><br />Frames exportados: <b className="text-[#241E18]">{forensic.frames_exported}</b></p>
          <p data-testid="forensic-fps">FPS original: <b className="text-[#241E18]">{forensic.fps_original}</b><br />FPS exportado: <b className="text-[#241E18]">{forensic.fps_exported}</b></p>
          <p data-testid="forensic-duration">Duración original: <b className="text-[#241E18]">{fmtForDur(forensic.duration_original)}</b><br />Duración exportada: <b className="text-[#241E18]">{fmtForDur(forensic.duration_exported)}</b></p>
        </div>
        <p
          data-testid="forensic-status"
          className={`mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11.5px] font-extrabold tracking-wide ${
            forensic.ok ? "bg-[#2FA45A] text-white" : "bg-[#D93025] text-white"
          }`}
        >
          {forensic.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
          Estado: {forensic.status}
        </p>
      </div>
    );

  const ProcLog = () =>
    procLog.length === 0 ? null : (
      <div data-testid="process-log" className="mt-4 rounded-2xl bg-[#FFF7F0] border border-[#F5E7D8] px-5 py-3.5 space-y-2">
        {procLog.map((r, i) => (
          <p key={i} className="text-[12.5px] font-semibold text-[#57503F] leading-relaxed">
            <span className="inline-flex items-center gap-1.5 mr-2">
              <Timer size={13} className="text-[#F26F21]" />
              <b className="text-[#C2570F]">{r.kind}</b>
            </span>
            Trabajo iniciado a las <b className="tabular-nums text-[#241E18]">{fmtTime(r.start)}</b> del{" "}
            <b className="tabular-nums text-[#241E18]">{fmtDate(r.start)}</b> y finalizado a las{" "}
            <b className="tabular-nums text-[#241E18]">{fmtTime(r.end)}</b> del{" "}
            <b className="tabular-nums text-[#241E18]">{fmtDate(r.end)}</b> · Tiempo total del proceso:{" "}
            <b className="tabular-nums text-[#C2570F]">{fmtElapsed(Math.floor((r.end - r.start) / 1000))}</b>
          </p>
        ))}
      </div>
    );
  const [searchParams] = useSearchParams();

  /* abrir un video existente desde la Mediateca (?video=id) sin volver a subirlo */
  useEffect(() => {
    const existingId = searchParams.get("video");
    if (!existingId) return;
    (async () => {
      try {
        setPhase("uploading");
        setUploadPct(80);
        const { data } = await axios.get(`${API}/videos/${existingId}`);
        if (!data.has_original) {
          toast.error("El original de este vídeo ya fue eliminado (regla de 24 h).");
          setPhase("idle");
          return;
        }
        setVideoId(data.id);
        setVideoName(data.name);
        setDuration(data.duration);
        setForensic(data.forensic_report || null);
        setPreviewOn(false);
        if (data.analysis_mode && BLUR_OPTIONS.some((o) => o.id === data.analysis_mode)) {
          setOption(data.analysis_mode);
        }
        setHasAnalysis(!!data.has_analysis);
        if (data.status === "done" && data.has_processed) {
          setShowOriginal(false);
          setPhase("done");
        } else if (data.has_analysis) {
          setPhase("review");
        } else {
          setPhase("config");
        }
        toast.success(`"${data.name}" cargado desde tu Mediateca`);
      } catch {
        toast.error("No se pudo cargar el vídeo de la Mediateca.");
        setPhase("idle");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const upd = () => setCreditsState(getCredits());
    window.addEventListener("naturblur-credits", upd);
    return () => {
      window.removeEventListener("naturblur-credits", upd);
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);

  const cost = Math.max(1, Math.ceil(duration));
  const insufficient = cost > credits;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const selected = BLUR_OPTIONS.find((o) => o.id === option);

  /* ---------- subida por chunks ---------- */
  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    if (!/\.(mp4|mov|m4v)$/i.test(file.name)) {
      toast.error("Formato no soportado. Usa MP4, MOV o M4V.");
      return;
    }
    if (file.size > 10 * 1024 * 1024 * 1024) {
      toast.error("El archivo supera los 10 GB permitidos.");
      return;
    }
    try {
      setPhase("uploading");
      setUploadPct(0);
      const { data: init } = await axios.post(`${API}/uploads/init`, { filename: file.name });
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", i);
        fd.append("chunk", blob, "chunk");
        await axios.post(`${API}/uploads/chunk`, fd);
        setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
      }
      const { data: done } = await axios.post(`${API}/uploads/complete`, {
        upload_id: init.upload_id,
        filename: file.name,
      });
      setVideoId(done.video_id);
      setVideoName(done.name);
      setDuration(done.duration);
      setPreviewOn(false);
      if (done.optimizing) {
        setPhase("processing");
        setProgress(10);
        setStepLabel("Optimizando vídeo gigante sin tocar frames ni FPS");
        toast(`${file.name} subido. Comprimiendo a alta calidad (frames y FPS intactos)…`);
        pollRef.current = setInterval(async () => {
          try {
            const { data } = await axios.get(`${API}/videos/${done.video_id}/status`);
            setStepLabel(data.step || "Optimizando vídeo gigante");
            setProgress(data.progress || 10);
            setJobPaused(!!data.paused);
            setJobStalled(data.stalled_secs || 0);
            if (data.status === "ready") {
              clearInterval(pollRef.current);
              setPhase("config");
              toast.success(data.step || "Vídeo optimizado y listo");
            }
          } catch {}
        }, 2000);
        return;
      }
      setPhase("config");
      toast.success(`${file.name} subido correctamente`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir el vídeo. Inténtalo de nuevo.");
      setPhase("idle");
    }
  }, []);

  const useSample = async () => {
    try {
      setPhase("uploading");
      setUploadPct(60);
      const { data } = await axios.post(`${API}/videos/sample`);
      setVideoId(data.video_id);
      setVideoName(data.name);
      setDuration(data.duration);
      setPreviewOn(false);
      setPhase("config");
      toast.success("Vídeo de muestra cargado");
    } catch (e) {
      toast.error("No se pudo cargar el vídeo de muestra.");
      setPhase("idle");
    }
  };

  /* ---------- control del trabajo en curso ---------- */
  const jobAction = async (action) => {
    if (!videoId) return;
    try {
      await axios.post(`${API}/videos/${videoId}/job/${action}`);
      if (action === "pause") { setJobPaused(true); toast("Trabajo en pausa. Puedes reanudarlo cuando quieras."); }
      if (action === "resume") { setJobPaused(false); toast.success("Trabajo reanudado"); }
      if (action === "cancel") toast("Cancelando… tu vídeo sigue intacto en la Mediateca.");
      if (action === "force") { setJobStalled(0); setJobPaused(false); toast.success("Reiniciado: retomando desde el último punto bueno"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo ejecutar la acción.");
    }
  };

  const onJobCancelled = (refund = false, backTo = "config") => {
    clearInterval(pollRef.current);
    stopTimer();
    if (refund) setCredits(getCredits() + cost);
    setJobPaused(false);
    setJobStalled(0);
    setPhase(backTo);
    toast(refund ? "Trabajo cancelado. Créditos devueltos." : "Trabajo cancelado.");
  };

  /* ---------- procesamiento real ---------- */
  const startProcessing = async () => {
    if (insufficient || !videoId) return;
    try {
      await axios.post(`${API}/videos/${videoId}/process`, { mode: option, style: `${style}|${strength}`, precision });
      setCredits(credits - cost);
      startTimer("Procesado directo");
      setPhase("processing");
      setJobPaused(false); setJobStalled(0); setFramesTotal(0); setFramesDone(0);
      setProgress(1);
      setStepLabel("Analizando fotogramas");
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/videos/${videoId}/status`);
          setProgress(data.progress || 0);
          setStepLabel(data.step || PROC_FALLBACK_STEP);
          setJobPaused(!!data.paused);
          setJobStalled(data.stalled_secs || 0);
          setFramesDone(data.frames_done || 0);
          setFramesTotal(data.frames_total || 0);
          updateEta(data.frames_done || 0, data.frames_total || 0);
          if (data.status === "cancelled") { onJobCancelled(true, "config"); return; }
          if (data.status === "done") {
            clearInterval(pollRef.current);
            stopTimer();
            setForensic(data.forensic || null);
            setShowOriginal(false);
            setPhase("done");
            toast.success("¡Tu vídeo está listo!");
          }
          if (data.status === "error") {
            clearInterval(pollRef.current);
            stopTimer();
            toast.error(`Error al procesar: ${data.step}`);
            setCredits(getCredits() + cost); // devolver créditos
            setPhase("config");
          }
        } catch {}
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el procesamiento.");
    }
  };

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setPhase("idle");
    setVideoId(null);
    setDuration(0);
    setPreviewOn(false);
    setProgress(0);
    setForensic(null);
    setHasAnalysis(false);
    setReviewChunk(null);
  };

  /* ---------- modo hibrido: analizar -> revisar -> renderizar ---------- */
  const startAnalyzePoll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/videos/${videoId}/status`);
        setProgress(data.progress || 0);
        setStepLabel(data.step || "Detectando elementos");
        setJobPaused(!!data.paused);
        setJobStalled(data.stalled_secs || 0);
        setFramesDone(data.frames_done || 0);
        setFramesTotal(data.frames_total || 0);
        updateEta(data.frames_done || 0, data.frames_total || 0);
        if (data.status === "cancelled") { onJobCancelled(false, "config"); return; }
        if (data.status === "analyzed") {
          clearInterval(pollRef.current);
          stopTimer();
          setHasAnalysis(true);
          setPhase((p) => (p === "review" ? p : "review"));
          toast.success(data.step || "Análisis completado");
        }
        if (data.status === "error") {
          clearInterval(pollRef.current);
          stopTimer();
          toast.error(`Error al analizar: ${data.step}. Tu vídeo sigue guardado en la Mediateca, no hace falta volver a subirlo.`);
          setPhase("config");
        }
      } catch {}
    }, 1500);
  };

  const startAnalysis = async () => {
    if (!videoId) return;
    try {
      const mode = option === "bg" ? "both" : option;
      await axios.post(`${API}/videos/${videoId}/analyze`, { mode, precision, chunk_minutes: chunkMinutes });
      startTimer("Análisis");
      setPhase("analyzing");
      setReviewChunk(null);
      setJobPaused(false); setJobStalled(0); setFramesTotal(0); setFramesDone(0);
      setProgress(1);
      setStepLabel("Detectando elementos");
      startAnalyzePoll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el análisis.");
    }
  };

  /* reanudar los tramos pendientes de una tarea (checkpoints) */
  const resumeTask = async () => {
    if (!videoId) return;
    try {
      await axios.post(`${API}/videos/${videoId}/task/continue`);
      startTimer("Análisis (reanudado)");
      setPhase("analyzing");
      setJobPaused(false); setJobStalled(0); setFramesTotal(0); setFramesDone(0);
      setProgress(1);
      setStepLabel("Reanudando tramos pendientes");
      startAnalyzePoll();
      toast.success("Tarea reanudada: los tramos ya analizados no se repiten");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reanudar la tarea.");
    }
  };

  /* abrir un tramo en el editor compacto */
  const openChunkReview = (chunk, task) => {
    setReviewChunk({ start: chunk.start, end: chunk.end, index: chunk.index, count: task.n_chunks });
    setHasAnalysis(true);
    setPhase("review");
  };

  const onRenderStarted = () => {
    setCredits(getCredits() - cost);
    startTimer("Renderizado");
    setPhase("processing");
    setJobPaused(false); setJobStalled(0); setFramesTotal(0); setFramesDone(0);
    setProgress(1);
    setStepLabel("Aplicando difuminado con seguimiento");
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/videos/${videoId}/status`);
        setProgress(data.progress || 0);
        setStepLabel(data.step || PROC_FALLBACK_STEP);
        setJobPaused(!!data.paused);
        setJobStalled(data.stalled_secs || 0);
        setFramesDone(data.frames_done || 0);
        setFramesTotal(data.frames_total || 0);
        updateEta(data.frames_done || 0, data.frames_total || 0);
        if (data.status === "cancelled") { onJobCancelled(true, "review"); return; }
        if (data.status === "done") {
          clearInterval(pollRef.current);
          stopTimer();
          setForensic(data.forensic || null);
          setShowOriginal(false);
          setPhase("done");
          toast.success("¡Tu vídeo está listo!");
        }
        if (data.status === "error") {
          clearInterval(pollRef.current);
          stopTimer();
          toast.error(`Error al renderizar: ${data.step}`);
          setCredits(getCredits() + cost);
          setPhase("review");
        }
      } catch {}
    }, 1500);
  };

  const originalSrc = videoId ? `${API}/videos/${videoId}/file/original` : "";
  const processedSrc = videoId ? `${API}/videos/${videoId}/file/processed` : "";

  /* ============ FASE: SUBIR ============ */
  if (phase === "idle" || phase === "uploading") {
    return (
      <main className="min-h-[calc(100vh-72px)] bg-white py-12 sm:py-16">
        <div className="max-w-[1080px] mx-auto px-4 sm:px-6">
          <Wizard current={0} />
          <div className="grid lg:grid-cols-[1fr_1.25fr] gap-6 items-stretch">
            {/* panel informativo */}
            <div className="rounded-3xl bg-[#FFF7F0] border border-[#F5E7D8] p-8 flex flex-col">
              <h1 className="font-display text-[28px] sm:text-[34px] font-extrabold text-[#241E18] leading-tight">
                Sube tu vídeo y difumina lo que quieras
              </h1>
              <p className="text-[14.5px] text-[#8A7F73] mt-3">
                Matrículas, caras, fondos u objetos. La IA hace el trabajo, tú solo eliges.
              </p>
              <ul className="mt-6 space-y-3.5 flex-1">
                {[
                  { icon: Film, txt: "Formatos MP4, MOV y M4V · hasta 10 GB (los >2 GB se optimizan al subir, frames intactos)" },
                  { icon: Timer, txt: "1 crédito por segundo de vídeo procesado" },
                  { icon: ShieldCheck, txt: "Tus archivos se eliminan a las 24 horas" },
                ].map(({ icon: Icon, txt }) => (
                  <li key={txt} className="flex items-center gap-3 text-[13.5px] font-semibold text-[#57503F]">
                    <span className="w-9 h-9 rounded-xl bg-white border border-[#F5E7D8] flex items-center justify-center text-[#F26F21] shrink-0">
                      <Icon size={16} />
                    </span>
                    {txt}
                  </li>
                ))}
              </ul>
              <button
                data-testid="use-sample-btn"
                onClick={useSample}
                disabled={phase === "uploading"}
                className="mt-6 w-full rounded-2xl border-2 border-[#F26F21]/30 bg-white p-3.5 flex items-center gap-3.5 hover:border-[#F26F21] hover:shadow-lg hover:shadow-[#F26F21]/10 transition-all group disabled:opacity-50"
              >
                <div className="relative w-20 h-12 rounded-xl overflow-hidden shrink-0">
                  <img src={IMAGES.street} alt="muestra" className="w-full h-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/15 transition-colors">
                    <Play size={16} className="text-white fill-white" />
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-[13.5px] font-extrabold text-[#241E18]">Probar con vídeo de muestra</p>
                  <p className="text-[12px] text-[#8A7F73]">Tráfico real con matrículas · sin subir nada</p>
                </div>
              </button>
            </div>

            {/* dropzone */}
            <div className="flex flex-col">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFile(e.dataTransfer.files?.[0]); }}
              onClick={() => phase === "idle" && inputRef.current?.click()}
              className={`rounded-3xl border-2 border-dashed px-6 py-16 flex flex-col items-center justify-center text-center transition-all duration-200 ${
                phase === "uploading"
                  ? "border-[#F26F21] bg-[#FFF3E9]"
                  : dragging
                  ? "border-[#F26F21] bg-[#FFF3E9] scale-[1.01] cursor-pointer"
                  : "border-[#EBDCCB] bg-white hover:border-[#F26F21] cursor-pointer"
              }`}
            >
              <input
                ref={inputRef}
                data-testid="upload-file-input"
                type="file"
                accept=".mp4,.mov,.m4v,video/mp4,video/quicktime"
                className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])}
              />
              {phase === "uploading" ? (
                <>
                  <Loader2 size={40} className="text-[#F26F21] animate-spin mb-5" />
                  <p className="text-[17px] font-extrabold text-[#241E18]">Subiendo tu vídeo…</p>
                  <div className="w-full max-w-xs h-2.5 rounded-full bg-[#F5EDE3] overflow-hidden mt-5">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#FFB81C] to-[#F26F21] transition-all duration-200" style={{ width: `${uploadPct}%` }} />
                  </div>
                  <p className="text-[13px] font-bold text-[#F26F21] mt-2.5">{uploadPct}%</p>
                </>
              ) : (
                <>
                  <span className="w-[72px] h-[72px] rounded-3xl bg-gradient-to-br from-[#FFB81C] to-[#F26F21] flex items-center justify-center mb-6 shadow-lg shadow-[#F26F21]/25">
                    <UploadCloud size={32} className="text-white" />
                  </span>
                  <p className="text-[19px] font-extrabold text-[#241E18]">Arrastra tu vídeo aquí</p>
                  <p className="text-[13.5px] text-[#8A7F73] mt-2">o haz clic para elegirlo desde tu equipo</p>
                  <span className="mt-7 inline-flex items-center gap-2 h-11 px-7 rounded-full bg-[#F26F21] text-white text-[14px] font-bold hover:bg-[#D95B12] transition-colors">
                    Elegir vídeo
                  </span>
                </>
              )}
            </div>
            <DriveCard />
            </div>
          </div>
        </div>
      </main>
    );
  }

  /* ============ FASE: REVISIÓN HÍBRIDA ============ */
  const gotoChunk = async (delta) => {
    if (!reviewChunk) return;
    try {
      const { data } = await axios.get(`${API}/videos/${videoId}`);
      const target = (data.chunks || []).find((c) => c.index === reviewChunk.index + delta);
      if (!target) return;
      if (["pending", "analyzing"].includes(target.status)) { toast("Ese tramo aún no está analizado."); return; }
      openChunkReview(target, data.task);
    } catch {}
  };

  const validateCurrentChunk = async () => {
    if (!reviewChunk) return;
    try {
      const { data } = await axios.post(`${API}/videos/${videoId}/chunks/${reviewChunk.index}/validate`);
      toast.success(data.validated
        ? `Tramo ${reviewChunk.index}/${reviewChunk.count} validado ✓ — ya puede exportarse`
        : `Validación del tramo ${reviewChunk.index}/${reviewChunk.count} retirada`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo validar el tramo.");
    }
  };

  if (phase === "review") {
    return (
      <main className="min-h-[calc(100vh-72px)] bg-white py-10">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6">
          <Wizard current={1} />
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="font-display text-[24px] sm:text-[28px] font-extrabold text-[#241E18] leading-tight">
                {reviewChunk
                  ? `Tramo ${reviewChunk.index}/${reviewChunk.count} — como un vídeo nuevo`
                  : option === "manual" ? "Dibuja y Dakota seguirá el objeto" : "Revisa lo que la IA ha detectado"}
              </h1>
              <p className="text-[13.5px] text-[#8A7F73] mt-1">
                {reviewChunk
                  ? "Editor compacto: solo ves los elementos de este tramo. Ni un fotograma se pierde — el rango es exacto."
                  : option === "manual"
                  ? "La primera decisión es tuya: marca una región, enséñasela a Dakota y ella la seguirá hasta que desaparezca."
                  : "Arrastra para corregir, añade óvalos o recuadros donde falte y elimina lo que sobre. Después, renderiza."}
              </p>
            </div>
            <button
              onClick={() => setPhase("config")}
              className="h-10 px-5 rounded-full border-2 border-[#F0E2D2] bg-white text-[13.5px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all"
            >
              ← Volver a configurar
            </button>
          </div>
          {reviewChunk && (
            <div data-testid="chunk-review-bar" className="flex flex-wrap items-center gap-2.5 mb-5 rounded-2xl bg-[#241E18] px-4 py-2.5">
              <span className="text-[12.5px] font-extrabold text-[#FFB81C] tabular-nums">
                Tramo {reviewChunk.index}/{reviewChunk.count} · fotogramas {reviewChunk.start}–{reviewChunk.end}
              </span>
              <span className="flex-1" />
              <button data-testid="chunk-prev-btn" onClick={() => gotoChunk(-1)} disabled={reviewChunk.index <= 1}
                className="h-8 px-3 rounded-lg bg-white/10 text-white text-[12px] font-bold hover:bg-white/20 disabled:opacity-30 transition-colors">
                ◀ Tramo anterior
              </button>
              <button data-testid="chunk-next-btn" onClick={() => gotoChunk(1)} disabled={reviewChunk.index >= reviewChunk.count}
                className="h-8 px-3 rounded-lg bg-white/10 text-white text-[12px] font-bold hover:bg-white/20 disabled:opacity-30 transition-colors">
                Tramo siguiente ▶
              </button>
              <button data-testid="chunk-validate-current-btn" onClick={validateCurrentChunk}
                className="h-8 px-4 rounded-lg bg-[#2FA45A] text-white text-[12px] font-extrabold hover:bg-[#268A4B] transition-colors inline-flex items-center gap-1.5">
                <CheckCircle2 size={13} /> Validar tramo
              </button>
              <button data-testid="chunk-exit-btn" onClick={() => setReviewChunk(null)}
                className="h-8 px-3 rounded-lg bg-white/10 text-white text-[12px] font-bold hover:bg-white/20 transition-colors">
                Ver vídeo completo
              </button>
            </div>
          )}
          <ReviewEditor
            videoId={videoId}
            style={style}
            setStyle={setStyle}
            strength={strength}
            setStrength={setStrength}
            cost={cost}
            credits={credits}
            mode={option}
            frameRange={reviewChunk}
            onRenderStarted={onRenderStarted}
          />
          <ChunkPanel
            videoId={videoId}
            style={style}
            strength={strength}
            activeIndex={reviewChunk?.index}
            onReviewChunk={openChunkReview}
            onResume={resumeTask}
          />
          <ProcLog />
        </div>
      </main>
    );
  }

  /* ============ FASES: CONFIG / PROCESANDO / LISTO ============ */
  return (
    <main className="min-h-[calc(100vh-72px)] bg-white py-10">
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6">
        <Wizard current={phase === "done" ? 2 : 1} />

        <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
          {/* IZQUIERDA: opciones */}
          <aside className="rounded-3xl bg-[#FFF7F0] border border-[#F5E7D8] p-6">
            <p className="text-[11.5px] font-extrabold tracking-[0.14em] uppercase text-[#C2570F]">Qué difuminar</p>
            <p className="text-[13px] text-[#8A7F73] mt-1 mb-5">Elige un modo de protección</p>
            <div className="space-y-2.5">
              {BLUR_OPTIONS.map((opt) => {
                const Icon = ICONS[opt.icon];
                const isSel = option === opt.id;
                return (
                  <button
                    key={opt.id}
                    data-testid={`blur-option-${opt.id}`}
                    onClick={() => {
                      setOption(opt.id);
                      if (phase === "done") { setPhase("config"); setPreviewOn(true); }
                    }}
                    className={`relative w-full flex items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left transition-all duration-200 ${
                      isSel ? "bg-white border-2 border-[#F26F21] shadow-md shadow-[#F26F21]/10" : "bg-white/60 border border-[#F0E2D2] hover:border-[#F26F21]/50"
                    }`}
                  >
                    <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                      isSel ? "bg-[#F26F21] text-white" : "bg-[#FFF3E9] text-[#F26F21]"
                    }`}>
                      <Icon size={20} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-[14.5px] font-extrabold text-[#241E18]">{opt.label}</span>
                        {opt.isNew && (
                          <span className="px-2 py-0.5 rounded-full bg-[#241E18] text-[#FFB81C] text-[9.5px] font-extrabold tracking-wide">NUEVO</span>
                        )}
                      </span>
                      <span className="block text-[12px] text-[#8A7F73] truncate">{opt.desc}</span>
                    </span>
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      isSel ? "border-[#F26F21] bg-[#F26F21]" : "border-[#E4D5C3]"
                    }`}>
                      {isSel && <span className="w-2 h-2 rounded-full bg-white" />}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* estilo de cobertura */}
            {(option === "faces" || option === "plates" || option === "both") && (
              <div className="mt-5">
                <p className="text-[11.5px] font-extrabold tracking-[0.14em] uppercase text-[#C2570F] mb-2.5">Estilo de cobertura</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "box", label: "Recuadro", hint: "Color del coche" },
                    { id: "pixel", label: "Pixelado", hint: "Mosaico censura" },
                    { id: "oval", label: "Ovalado", hint: "Difuminado suave" },
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`rounded-xl px-2.5 py-3 text-center transition-all ${
                        style === s.id ? "bg-white border-2 border-[#F26F21] shadow-sm" : "bg-white/60 border border-[#F0E2D2] hover:border-[#F26F21]/50"
                      }`}
                    >
                      <span className="flex justify-center mb-1.5">
                        {s.id === "box" && <span className="w-6 h-3.5 rounded-[3px] bg-[#808080] inline-block" />}
                        {s.id === "pixel" && (
                          <span className="flex gap-[2px]">
                            {["#9a9a9a", "#c4c4c4", "#7d7d7d"].map((c, i) => (
                              <span key={i} className="w-2 h-3.5 inline-block rounded-[2px]" style={{ backgroundColor: c }} />
                            ))}
                          </span>
                        )}
                        {s.id === "oval" && <span className="w-6 h-3.5 rounded-full bg-[#b8b8b8] blur-[1.5px] inline-block" />}
                      </span>
                      <span className="block text-[12px] font-extrabold text-[#241E18]">{s.label}</span>
                      <span className="block text-[9.5px] text-[#8A7F73]">{s.hint}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* intensidad del difuminado */}
            {(option === "faces" || option === "plates" || option === "both") && (
              <div className="mt-5">
                <p className="text-[11.5px] font-extrabold tracking-[0.14em] uppercase text-[#C2570F] mb-2.5">Intensidad</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 1, label: "Suave", hint: "Ligero" },
                    { id: 2, label: "Media", hint: "Equilibrado" },
                    { id: 3, label: "Fuerte", hint: "Irreconocible" },
                  ].map((s) => (
                    <button
                      key={s.id}
                      data-testid={`strength-${s.id}-btn`}
                      onClick={() => setStrength(s.id)}
                      className={`rounded-xl px-2.5 py-3 text-center transition-all ${
                        strength === s.id ? "bg-white border-2 border-[#F26F21] shadow-sm" : "bg-white/60 border border-[#F0E2D2] hover:border-[#F26F21]/50"
                      }`}
                    >
                      <span className="block text-[12px] font-extrabold text-[#241E18]">{s.label}</span>
                      <span className="block text-[9.5px] text-[#8A7F73]">{s.hint}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* modo de deteccion */}
            {(option === "plates" || option === "both") && (
              <div className="mt-5">
                <p className="text-[11.5px] font-extrabold tracking-[0.14em] uppercase text-[#C2570F] mb-2.5">Modo de detección</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="precision-fast-btn"
                    onClick={() => setPrecision("fast")}
                    className={`rounded-xl px-2.5 py-3 text-center transition-all ${
                      precision === "fast" ? "bg-white border-2 border-[#F26F21] shadow-sm" : "bg-white/60 border border-[#F0E2D2] hover:border-[#F26F21]/50"
                    }`}
                  >
                    <span className="block text-[12.5px] font-extrabold text-[#241E18]">Rápido</span>
                    <span className="block text-[9.5px] text-[#8A7F73]">Proxy + seguimiento</span>
                  </button>
                  <button
                    data-testid="precision-max-btn"
                    onClick={() => setPrecision("max")}
                    className={`rounded-xl px-2.5 py-3 text-center transition-all ${
                      precision === "max" ? "bg-white border-2 border-[#F26F21] shadow-sm" : "bg-white/60 border border-[#F0E2D2] hover:border-[#F26F21]/50"
                    }`}
                  >
                    <span className="block text-[12.5px] font-extrabold text-[#241E18]">Precisión Máxima</span>
                    <span className="block text-[9.5px] text-[#8A7F73]">IA en cada fotograma</span>
                  </button>
                </div>
                {precision === "max" && (
                  <p data-testid="precision-max-hint" className="mt-2 text-[11px] font-semibold text-[#92600A] bg-[#FEF6E7] border border-[#F5DFAE] rounded-lg px-3 py-2">
                    Busca matrículas en todos los fotogramas del vídeo original. La anonimización nunca depende del OCR: si la matrícula existe visualmente, se difumina. Es más lento pero maximiza la tasa de anonimización.
                  </p>
                )}

                <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#C2570F] mt-4 mb-2">Tramos de trabajo (checkpoints)</p>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 5].map((m) => (
                    <button
                      key={m}
                      data-testid={`chunk-minutes-${m}`}
                      onClick={() => setChunkMinutes(m)}
                      className={`rounded-xl px-2 py-2.5 text-center transition-all ${
                        chunkMinutes === m ? "bg-white border-2 border-[#F26F21] shadow-sm" : "bg-white/60 border border-[#F0E2D2] hover:border-[#F26F21]/50"
                      }`}
                    >
                      <span className="block text-[12.5px] font-extrabold text-[#241E18]">{m} min</span>
                      <span className="block text-[9.5px] text-[#8A7F73]">por tramo</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[10.5px] font-semibold text-[#8A7F73]">
                  El vídeo se analiza tramo a tramo: cada tramo listo se puede revisar, validar y exportar sin esperar al resto.
                </p>
              </div>
            )}

            {/* resumen */}
            <div className="mt-6 rounded-2xl bg-white border border-[#F0E2D2] p-4 space-y-2.5">
              <div className="flex justify-between text-[13px]">
                <span className="text-[#8A7F73] font-semibold">Archivo</span>
                <span className="font-bold text-[#241E18] truncate max-w-[180px]">{videoName}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-[#8A7F73] font-semibold">Duración</span>
                <span className="font-bold text-[#241E18]">{fmt(duration)}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-[#8A7F73] font-semibold">Coste</span>
                <span className="inline-flex items-center gap-1 font-extrabold text-[#C2570F]">
                  <Coins size={13} /> {cost} créditos
                </span>
              </div>
              <div className="flex justify-between text-[13px] pt-2 border-t border-[#F5EDE3]">
                <span className="text-[#8A7F73] font-semibold">Tu saldo</span>
                <span className={`font-extrabold ${insufficient ? "text-[#D93025]" : "text-[#2FA45A]"}`}>{credits} créditos</span>
              </div>
            </div>

            {insufficient && phase === "config" && (
              <div className="mt-4 rounded-2xl bg-[#FDECEA] border border-[#F5C6C0] px-4 py-3 flex items-start gap-2.5">
                <AlertTriangle size={16} className="text-[#D93025] mt-0.5 shrink-0" />
                <p className="text-[12.5px] font-semibold text-[#A4271C]">
                  Saldo insuficiente.{" "}
                  <Link to="/tarifas" className="underline font-extrabold">Consigue más créditos →</Link>
                </p>
              </div>
            )}
          </aside>

          {/* DERECHA: vídeo */}
          <section>
            {phase === "processing" || phase === "analyzing" ? (
              <>
              <div className="rounded-3xl bg-[#241E18] px-8 py-20 flex flex-col items-center text-center">
                <div className="relative mb-7">
                  <Loader2 size={46} className={`text-[#FFB81C] ${jobPaused ? "opacity-40" : "animate-spin"}`} />
                </div>
                <p className="text-white font-display text-[22px] font-extrabold mb-1">{stepLabel}…</p>
                <p className="text-white/45 text-[13px] mb-9">
                  {phase === "analyzing" ? "Análisis para revisión" : selected?.label} · {fmt(duration)} · IA con seguimiento de movimiento
                </p>
                <div className="w-full max-w-md h-3 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#FFB81C] to-[#F26F21] transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
                {framesTotal > 0 && (
                  <p data-testid="frame-counter" className="text-white/75 text-[13px] font-bold mt-4 tabular-nums">
                    Fotograma procesado {Number(framesDone).toLocaleString("es-ES")} / {Number(framesTotal).toLocaleString("es-ES")}
                    {etaMin != null && !jobPaused && (
                      <span data-testid="eta-label" className="text-[#FFB81C]/90"> · ~{etaMin} min restantes</span>
                    )}
                  </p>
                )}
                <p className="text-[#FFB81C] text-[14px] font-extrabold mt-3">{Math.floor(progress)}%</p>
                {/* controles del trabajo en curso */}
                <div className="flex flex-wrap items-center justify-center gap-2.5 mt-6">
                  <button
                    data-testid="job-pause-btn"
                    onClick={() => jobAction(jobPaused ? "resume" : "pause")}
                    className="h-10 px-5 rounded-full bg-white/10 border border-white/15 text-white text-[13px] font-bold hover:bg-white/20 transition-colors inline-flex items-center gap-2"
                  >
                    {jobPaused ? <Play size={14} /> : <Pause size={14} />}
                    {jobPaused ? "Reanudar" : "Pausar"}
                  </button>
                  <button
                    data-testid="job-force-btn"
                    onClick={() => jobAction("force")}
                    className="h-10 px-5 rounded-full bg-white/10 border border-white/15 text-white text-[13px] font-bold hover:bg-white/20 transition-colors inline-flex items-center gap-2"
                    title="Cancela y relanza el trabajo retomando desde el último punto bueno"
                  >
                    <Zap size={14} /> Forzar reinicio
                  </button>
                  <button
                    data-testid="job-cancel-btn"
                    onClick={() => jobAction("cancel")}
                    className="h-10 px-5 rounded-full border border-[#E4574A]/40 text-[#FF8A7E] text-[13px] font-bold hover:bg-[#E4574A]/15 transition-colors inline-flex items-center gap-2"
                  >
                    <XCircle size={14} /> Cancelar
                  </button>
                </div>
                {jobPaused && (
                  <p data-testid="job-paused-badge" className="mt-4 px-4 py-1.5 rounded-full bg-[#FFB81C]/15 border border-[#FFB81C]/30 text-[#FFB81C] text-[12px] font-extrabold uppercase tracking-wider">
                    En pausa — el progreso se conserva
                  </p>
                )}
                {jobStalled >= 90 && !jobPaused && (
                  <div data-testid="job-stall-warning" className="mt-5 max-w-md rounded-2xl bg-[#E4574A]/10 border border-[#E4574A]/35 px-5 py-4 text-left">
                    <p className="text-[#FF8A7E] text-[13px] font-extrabold inline-flex items-center gap-2 mb-1">
                      <AlertTriangle size={15} /> Posible cuelgue detectado
                    </p>
                    <p className="text-white/70 text-[12.5px]">
                      El paso «{stepLabel}» lleva <span className="font-extrabold text-white">{jobStalled}s</span> sin dar señales de vida.
                      Motivo probable: {stallHint(stepLabel)}.
                    </p>
                    <p className="text-white/50 text-[12px] mt-1.5">
                      Usa <span className="font-bold text-white/80">«Forzar reinicio»</span>: retoma desde el último punto bueno sin repetir lo ya procesado.
                    </p>
                  </div>
                )}
                {/* cronometro del proceso */}
                <div className="mt-7 rounded-2xl bg-white/5 border border-white/10 px-6 py-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-8">
                  <div className="text-center">
                    <p className="text-white/40 text-[10.5px] font-extrabold uppercase tracking-[0.14em]">Inicio</p>
                    <p className="text-white text-[13.5px] font-bold tabular-nums">{fmtDateTime(procStart)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white/40 text-[10.5px] font-extrabold uppercase tracking-[0.14em]">Tiempo transcurrido</p>
                    <p className="text-[#FFB81C] font-display text-[26px] font-extrabold tabular-nums leading-none">{fmtElapsed(elapsed)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white/40 text-[10.5px] font-extrabold uppercase tracking-[0.14em]">Fin</p>
                    <p className="text-white/50 text-[13.5px] font-bold">en curso…</p>
                  </div>
                </div>
                <p className="text-white/35 text-[12px] mt-6 max-w-sm">
                  {phase === "analyzing"
                    ? "La IA está proponiendo las zonas a difuminar. Después podrás corregirlas antes de renderizar."
                    : "Estamos difuminando de verdad cada fotograma de tu vídeo. Esto puede tardar un poco según la duración."}
                </p>
              </div>
              {videoId && (phase === "analyzing" || phase === "processing") && (
                <ChunkPanel
                  videoId={videoId}
                  style={style}
                  strength={strength}
                  onReviewChunk={openChunkReview}
                  onResume={resumeTask}
                />
              )}
              </>
            ) : (              <>
                {phase === "done" && (
                  <div className="flex items-center gap-3 mb-5 rounded-2xl bg-[#EAF7EE] border border-[#BFE5CB] px-5 py-3.5">
                    <CheckCircle2 size={20} className="text-[#2FA45A] shrink-0" />
                    <p className="text-[14px] font-bold text-[#1E6B3A]">
                      ¡Listo! Se ha aplicado <span className="underline decoration-[#2FA45A]/40">{selected?.label}</span> con IA real a tu vídeo.
                    </p>
                  </div>
                )}
                <div className="relative rounded-3xl overflow-hidden bg-black shadow-xl shadow-black/10">
                  <video
                    key={phase === "done" && !showOriginal ? processedSrc : originalSrc}
                    src={phase === "done" && !showOriginal ? processedSrc : originalSrc}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full block max-h-[460px] object-contain bg-black"
                  />
                  {phase === "config" && previewOn && <SimPatches option={option} />}
                  <CornerTag
                    label={phase === "done" ? (showOriginal ? "Original" : "Procesado") : previewOn ? "Simulación" : "Original"}
                    tone={phase === "done" && !showOriginal ? "orange" : "light"}
                  />
                </div>

                {/* registro persistente del proceso, debajo del vídeo */}
                <ProcLog />
                {phase === "done" && <ForensicPanel />}
                {videoId && (phase === "done" || phase === "config") && (
                  <ChunkPanel
                    videoId={videoId}
                    style={style}
                    onReviewChunk={openChunkReview}
                    onResume={resumeTask}
                  />
                )}
                <DriveCard />

                {/* acciones */}
                {phase === "config" && (
                  <div className="flex flex-wrap items-center gap-3 mt-6">
                    <button
                      data-testid="analyze-review-btn"
                      onClick={startAnalysis}
                      disabled={option === "bg"}
                      title={option === "bg" ? "El modo Fondo no necesita revisión" : ""}
                      className={`h-12 px-7 rounded-full text-[14.5px] font-extrabold inline-flex items-center gap-2 transition-all ${
                        option === "bg"
                          ? "bg-[#EFE6DC] text-[#B4A896] cursor-not-allowed"
                          : "bg-[#F26F21] text-white hover:bg-[#D95B12] hover:shadow-xl hover:shadow-[#F26F21]/25"
                      }`}
                    >
                      <Sparkles size={16} />
                      {option === "manual" ? "Abrir editor manual" : "Analizar y revisar"}
                    </button>
                    <button
                      data-testid="direct-process-btn"
                      onClick={startProcessing}
                      disabled={insufficient || option === "manual"}
                      title={option === "manual" ? "El modo manual requiere el editor" : ""}
                      className={`h-12 px-6 rounded-full text-[14px] font-bold inline-flex items-center gap-2 transition-all ${
                        insufficient || option === "manual"
                          ? "bg-[#EFE6DC] text-[#B4A896] cursor-not-allowed border-2 border-transparent"
                          : "border-2 border-[#241E18] bg-white text-[#241E18] hover:bg-[#241E18] hover:text-white"
                      }`}
                    >
                      {insufficient ? "Saldo insuficiente" : "Procesado directo"}
                    </button>
                    <button
                      onClick={() => setPreviewOn((v) => !v)}
                      className="h-12 px-6 rounded-full border-2 border-[#F0E2D2] bg-white text-[14px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center gap-2"
                    >
                      {previewOn ? <EyeOff size={16} /> : <Eye size={16} />}
                      {previewOn ? "Ocultar simulación" : "Ver simulación"}
                    </button>
                    <button onClick={reset} className="h-12 px-4 text-[13.5px] font-bold text-[#8A7F73] hover:text-[#F26F21] transition-colors inline-flex items-center gap-1.5">
                      <RotateCcw size={14} /> Cambiar vídeo
                    </button>
                  </div>
                )}

                {phase === "done" && (
                  <div className="flex flex-wrap items-center gap-3 mt-6">
                    <a
                      href={`${processedSrc}?download=1`}
                      className="h-12 px-7 rounded-full bg-[#F26F21] text-white text-[14.5px] font-extrabold hover:bg-[#D95B12] hover:shadow-xl hover:shadow-[#F26F21]/25 transition-all inline-flex items-center gap-2"
                    >
                      <Download size={16} /> Descargar vídeo
                    </a>
                    <button
                      onClick={() => setShowOriginal((v) => !v)}
                      className="h-12 px-6 rounded-full border-2 border-[#F0E2D2] bg-white text-[14px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center gap-2"
                    >
                      {showOriginal ? <Eye size={16} /> : <EyeOff size={16} />}
                      {showOriginal ? "Ver procesado" : "Comparar con original"}
                    </button>
                    {hasAnalysis && (
                      <button
                        data-testid="back-to-review-btn"
                        onClick={() => setPhase("review")}
                        className="h-12 px-6 rounded-full border-2 border-[#F0E2D2] bg-white text-[14px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center gap-2"
                      >
                        <Sparkles size={16} /> Volver a revisar
                      </button>
                    )}
                    <button onClick={reset} className="h-12 px-4 text-[13.5px] font-bold text-[#8A7F73] hover:text-[#F26F21] transition-colors inline-flex items-center gap-1.5">
                      <RotateCcw size={14} /> Procesar otro vídeo
                    </button>
                  </div>
                )}

                <div className="flex items-start gap-2.5 mt-7">
                  <ShieldCheck size={17} className="text-[#F26F21] mt-0.5 shrink-0" />
                  <p className="text-[12.5px] text-[#8A7F73]">
                    <span className="font-bold text-[#57503F]">Privacidad garantizada:</span> tus vídeos se eliminan automáticamente de nuestros servidores a las 24 horas.
                  </p>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default EditorPage;
